import React, { useState, useEffect, useMemo } from "react";
import { db } from "@/firebase";
import {
  collection,
  doc,
  setDoc,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import {
  ShieldAlert,
  ShieldCheck,
  Lock,
  CheckCircle2,
  AlertTriangle,
  Users,
  Database,
  Smartphone,
  Globe,
  ArrowRight,
  MessageSquare,
  Copy,
  Check,
  Sparkles,
  LogOut,
  FileText,
  KeyRound,
  RefreshCw,
} from "lucide-react";

const COMMITTEE_MEMBERS = {
  "JORGE-360": {
    id: "jorge",
    code: "JORGE-360",
    name: "Jorge Batista",
    role: "Líder de Producto y Arquitectura WE360",
    badgeColor: "bg-[#00628F] text-white",
    borderColor: "border-[#00628F]",
    avatar: "JB",
  },
  "LEANDRO-360": {
    id: "leandro",
    code: "LEANDRO-360",
    name: "Ing. Leandro Mayor",
    role: "Líder de Core Services & Integración WhatsApp / FHIR",
    badgeColor: "bg-[#2A9D8F] text-white",
    borderColor: "border-[#2A9D8F]",
    avatar: "LM",
  },
  "ALVAREZ-360": {
    id: "alvarez",
    code: "ALVAREZ-360",
    name: "Dr. Miguel Álvarez",
    role: "Director Médico y Gobernanza Clínica WE360",
    badgeColor: "bg-amber-600 text-white",
    borderColor: "border-amber-600",
    avatar: "MA",
  },
};

const MEMBER_LIST = Object.values(COMMITTEE_MEMBERS);

const QUESTIONS = [
  {
    id: "q1_identity_isolation",
    number: 1,
    category: "Seguridad y Privacidad Clínica",
    title: "Aislamiento Estricto por Identidad Compuesta (Email + Teléfono)",
    question:
      "¿Qué debe ocurrir cuando el correo electrónico del usuario autenticado en Web NO coincide con el correo dueño de ese número de teléfono en Core Services o en GCP FHIR?",
    scenario: {
      title: "Escenario de Ejemplo: Cruce de recomendaciones por teléfono repetido",
      description:
        "Juan (juan@empresa.com) se registró hace una semana con el teléfono 507-6677-8899, completó F1–F5 y tiene recomendaciones nutricionales activas en WhatsApp. Hoy, Pedro (pedro@empresa.com) crea una cuenta nueva y en su F1 escribe por error (o intencionalmente) el mismo teléfono 507-6677-8899. Al abrir su Dashboard, la Web consulta a Core Services enviando phone=50766778899 & email=pedro@empresa.com.",
      incidentRef:
        "Caso real ocurrido en DEV: test20261020727@we360.app recibió el 'EMPUJÓN CLÍNICO ACTIVO: CENA LIGERA: ENSALADA CON PROTEÍNA (08:30 PM)' que pertenecía a test008@we360.net porque ambos tenían el teléfono 50766778899.",
    },
    impact: {
      web: "WhatsAppDailyFeedWidget oculta cualquier recomendación o activeNudge si hay discrepancia de identidad.",
      core: "Endpoints /api/user/daily-recommendations y /api/onboarding/state validan que request.email === record.email antes de retornar datos clínicos.",
      fhir: "Ningún recurso FHIR Patient puede fusionarse ni consultarse por telecom=phone si el telecom=email pertenece a otro UID.",
    },
    options: [
      {
        id: "A",
        recommended: true,
        label: "Opción A (Recomendada): Bloqueo Estricto + Alerta de Conflicto (Zero-Leak)",
        detail:
          "Core Services exige coincidencia exacta (email + phone). Si el teléfono pertenece a otro correo, devuelve 409 PHONE_OWNERSHIP_CONFLICT sin filtrar ningún dato clínico (ni recomendaciones, ni activeNudge, ni fase). El Dashboard muestra estado bloqueado seguro solicitando corregir o verificar su número.",
      },
      {
        id: "B",
        recommended: false,
        label: "Opción B: Ignorar silenciosamente el teléfono en conflicto y operar solo por Email",
        detail:
          "Si el teléfono ya pertenece a otro correo, Core Services ignora el parámetro phone y responde únicamente con lo que exista para el email (vacío), sin mostrar alerta de conflicto de teléfono al usuario.",
      },
      {
        id: "C",
        recommended: false,
        label: "Opción C: Otra regla / Ajuste personalizado (detallar en comentarios)",
        detail:
          "Definir una variante distinta en las observaciones del comité.",
      },
    ],
  },
  {
    id: "q2_global_uniqueness",
    number: 2,
    category: "Integridad de Registro",
    title: "Unicidad Global del Teléfono en Todos los Puntos de Entrada",
    question:
      "¿Dónde y contra qué bases de datos debe validarse que un número de teléfono sea único (1 Teléfono = 1 Usuario Activo)?",
    scenario: {
      title: "Escenario de Ejemplo: Registro por F1 saltándose la validación de Onboarding",
      description:
        "Actualmente, la pantalla de Onboarding (/onboarding) revisa en Firestore si el teléfono ya existe, pero el Formulario F1 (/f1) y Mi Cuenta (/mi-cuenta) guardan el teléfono directamente sin verificar si otro usuario ya lo tiene en Firestore, en Core Services o en GCP FHIR.",
      incidentRef:
        "Brecha actual: Un usuario que entra directo a /f1 o edita su perfil puede guardar un teléfono ya registrado por otro colaborador sin recibir error.",
    },
    impact: {
      web: "Unificar la validación pre-guardado en Onboarding, FormF1, MiCuenta e importación B2B.",
      core: "Índice único de teléfono activo en la base de datos de Core Services.",
      fhir: "Verificación de Patient?telecom=phone antes de asignar el teléfono a un nuevo recurso FHIR Patient.",
    },
    options: [
      {
        id: "A",
        recommended: true,
        label: "Opción A (Recomendada): Unicidad Global Tripartita (Firestore + Core Services + GCP FHIR)",
        detail:
          "Bloquear en todos los formularios (Onboarding, F1, Mi Cuenta y carga B2B) cualquier teléfono que ya esté vinculado a otro correo activo en cualquiera de las 3 capas. Mostrar mensaje claro: 'Este número ya está vinculado a otra cuenta en WE360'.",
      },
      {
        id: "B",
        recommended: false,
        label: "Opción B: Validar unicidad únicamente en Firestore Web",
        detail:
          "Aplicar el chequeo de Firestore en Onboarding, F1 y Mi Cuenta, sin consultar sincrónicamente a Core Services ni FHIR antes de guardar.",
      },
      {
        id: "C",
        recommended: false,
        label: "Opción C: Otra regla / Ajuste personalizado (detallar en comentarios)",
        detail:
          "Definir una variante distinta en las observaciones del comité.",
      },
    ],
  },
  {
    id: "q3_phone_verification_otp",
    number: 3,
    category: "Anti-Spoofing y Consentimiento",
    title: "Verificación de Propiedad del Teléfono (WhatsApp OTP / Handshake)",
    question:
      "¿Basta con que el usuario escriba un número de teléfono en F1 para activarle el canal de WhatsApp en el Dashboard, o debe demostrar que es dueño del número?",
    scenario: {
      title: "Escenario de Ejemplo: Usuario ingresa el celular de otra persona antes que ella",
      description:
        "El colaborador Carlos (carlos@banco.com) se registra antes que el Gerente General de su empresa y escribe en su F1 el celular del Gerente (507-6000-1111). Como el Gerente aún no se ha registrado, el número está libre. Si no hay verificación, Carlos vincularía ese teléfono a su cuenta y el bot le enviaría mensajes clínicos al WhatsApp del Gerente.",
      incidentRef:
        "Riesgo de privacidad: Cualquier usuario podría escribir un número ajeno libre y disparar mensajes de salud hacia terceros o capturar su historial de WhatsApp.",
    },
    impact: {
      web: "Campo phoneVerified: false hasta completar PIN o mensaje de activación; tarjeta en estado 'Pendiente de verificar en WhatsApp'.",
      core: "Envío de PIN OTP de 6 dígitos por WhatsApp o validación de wa_id entrante desde el dispositivo del usuario.",
      fhir: "El telecom=phone se marca como verificado solo tras completar el handshake.",
    },
    options: [
      {
        id: "A",
        recommended: true,
        label: "Opción A (Recomendada): Verificación Obligatoria por WhatsApp (PIN OTP o Mensaje de Activación)",
        detail:
          "Al guardar el teléfono en F1/Mi Cuenta, el canal queda en phoneVerified: false. Se envía un código de 4–6 dígitos por WhatsApp (o botón 'Enviar mensaje de activación al Bot'). Hasta verificarlo, el Dashboard no muestra datos de WhatsApp ni activa empujones automáticos.",
      },
      {
        id: "B",
        recommended: false,
        label: "Opción B: Activación por Primera Interacción desde el WhatsApp del Usuario (Sin OTP en Web)",
        detail:
          "No pedir código OTP en la Web, pero mantener el widget de WhatsApp en estado 'Inicia tu conversación en WhatsApp' hasta que el usuario escriba voluntariamente su primer 'Hola' al bot desde su propio celular.",
      },
      {
        id: "C",
        recommended: false,
        label: "Opción C: Confiar en el número digitado sin verificación (Solo validar que no esté repetido)",
        detail:
          "Basta con que el número no esté ocupado por otro usuario para darlo por válido inmediatamente.",
      },
    ],
  },
  {
    id: "q4_initial_whatsapp_state_f1",
    number: 4,
    category: "Experiencia Clínica en Dashboard",
    title: "Estado del Módulo 'Asistente de Bienestar en WhatsApp' con Solo F1",
    question:
      "Cuando un usuario nuevo acaba de crear su cuenta y solo ha completado F1 + Escaneo Facial (sin haber interactuado con WhatsApp ni completado el resto de su evaluación), ¿qué debe mostrar el módulo de WhatsApp en el Dashboard?",
    scenario: {
      title: "Escenario de Ejemplo: Usuario recién registrado ve un 'Empujón Clínico Activo' de cena",
      description:
        "Ana (ana@we360.app) acaba de llenar solo su F1 y su escaneo facial. Entra por primera vez al Dashboard. En la parte inferior ve el candado 'Evaluación en Progreso (Fase 1)', pero arriba del candado le aparece un banner verde brillante: 'EMPUJÓN CLÍNICO ACTIVO: CENA LIGERA: ENSALADA CON PROTEÍNA - 08:30 PM'. Ana se pregunta por qué el asistente de WhatsApp le está recomendando una cena si ella nunca ha hablado con el WhatsApp ni ha llenado nutrición (F5).",
      incidentRef:
        "En el código actual, WhatsAppDailyFeedWidget renderiza data.activeNudge fuera y por encima del bloqueo de estado vacío.",
    },
    impact: {
      web: "Condicionar la visualización de activeNudge y del feed de recomendaciones a los criterios clínicos aprobados.",
      core: "No devolver activeNudge pre-poblado para usuarios sin interacción real o sin perfil clínico suficiente.",
      fhir: "Alineación con la Regla #4 de Sinceridad de Datos (isDemo: false).",
    },
    options: [
      {
        id: "A",
        recommended: true,
        label: "Opción A (Recomendada): Estado honesto 'Conecta tu WhatsApp / Evaluación en Progreso' (Sin Empujones hasta verificar y perfilar)",
        detail:
          "Si el usuario es real (isDemo: false) y aún no ha verificado su WhatsApp o está en Fase inicial sin interacción real con el asistente, ocultar cualquier activeNudge o recomendación pre-cargada. Mostrar únicamente el llamado a la acción claro para vincular su WhatsApp y avanzar en sus formularios F2–F5.",
      },
      {
        id: "B",
        recommended: false,
        label: "Opción B: Permitir 1 Empujón de Bienvenida basado en F1 + Escaneo Facial desde el Día 1",
        detail:
          "Permitir que con solo F1 + Escaneo Facial se muestre un primer empujón clínico real (generado exclusivamente con los signos vitales de esa propia cuenta verificada), cambiando el texto inferior para no contradecirse con un candado de bloqueo.",
      },
      {
        id: "C",
        recommended: false,
        label: "Opción C: Exigir F1 a F5 completos (Fase 6) antes de habilitar cualquier recomendación o empujón de WhatsApp",
        detail:
          "Mantener el módulo 100% bloqueado (sin nudges ni recomendaciones) hasta que el usuario complete los 5 formularios clínicos (F1–F5).",
      },
    ],
  },
  {
    id: "q5_phone_recycling_and_deletion",
    number: 5,
    category: "Ciclo de Vida y Mantenimiento",
    title: "Reciclaje de Número Telefónico y Limpieza en Cascada al Eliminar Usuarios",
    question:
      "¿Cómo manejamos cuentas de prueba eliminadas (que dejan el teléfono 'huérfano' en Core Services) o el caso real donde un usuario cambia de número o la telefónica recicla su línea?",
    scenario: {
      title: "Escenario de Ejemplo: Cuenta de prueba borrada en AdminUsers pero viva en Core Services",
      description:
        "En ambiente DEV o Producción se elimina un usuario de prueba desde AdminUsers (/admin/usuarios) en Firestore y FHIR, pero su teléfono (50766778899) queda registrado en la base de datos de Core Services con el correo viejo. Cuando se intenta volver a usar ese teléfono en una prueba nueva, choca con el registro fantasma.",
      incidentRef:
        "Además, en la vida real, las operadoras telefónicas reciclan números inactivos tras varios meses o un colaborador puede cambiar su número personal en Mi Cuenta.",
    },
    impact: {
      web: "Flujo 'Reclamar mi número con código de WhatsApp' y botón de borrado en cascada en AdminUsers.",
      core: "Endpoint o webhook de purga/desvinculación de teléfono cuando se elimina un usuario o cuando un nuevo dueño valida OTP.",
      fhir: "Actualización sincronizada de Patient.telecom en Google Cloud Healthcare API.",
    },
    options: [
      {
        id: "A",
        recommended: true,
        label: "Opción A (Recomendada): Baja en Cascada Automática (Web + Core + FHIR) + Reclamo de Número vía OTP",
        detail:
          "1) Al eliminar un usuario en AdminUsers, se limpia su teléfono en Firestore, GCP FHIR y Core Services simultáneamente. 2) Si un usuario real intenta registrar un número que aparece ocupado pero demuestra con un código OTP en vivo que él tiene el chip de WhatsApp en su mano, el sistema desvincula el teléfono de la cuenta antigua inactiva y lo asigna a la nueva.",
      },
      {
        id: "B",
        recommended: false,
        label: "Opción B: Liberación exclusivamente manual mediante Soporte / Administrador",
        detail:
          "Incluir la baja en cascada en AdminUsers al borrar usuarios, pero si un teléfono está ocupado por otra cuenta activa, solo un SuperAdmin puede liberarlo o reasignarlo manualmente.",
      },
      {
        id: "C",
        recommended: false,
        label: "Opción C: Otra regla / Ajuste personalizado (detallar en comentarios)",
        detail:
          "Definir una variante distinta en las observaciones del comité.",
      },
    ],
  },
];

export default function CommitteeRulesVote({ initialCode = "" } = {}) {
  const [accessCodeInput, setAccessCodeInput] = useState("");
  const [codeError, setCodeError] = useState("");
  const [activeMember, setActiveMember] = useState(() => {
    const propCode = (initialCode || "").trim().toUpperCase();
    if (propCode && COMMITTEE_MEMBERS[propCode]) {
      return COMMITTEE_MEMBERS[propCode];
    }
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search || "");
      const urlCode = (params.get("code") || "").trim().toUpperCase();
      if (urlCode && COMMITTEE_MEMBERS[urlCode]) {
        return COMMITTEE_MEMBERS[urlCode];
      }
      const savedCode = sessionStorage.getItem("we360_committee_code");
      if (savedCode && COMMITTEE_MEMBERS[savedCode]) {
        return COMMITTEE_MEMBERS[savedCode];
      }
    }
    return null;
  });
  const [activeDiagramTab, setActiveDiagramTab] = useState("incident");

  // Local vote state for current member
  const [selections, setSelections] = useState({});
  const [comments, setComments] = useState({});
  const [generalNote, setGeneralNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [copiedMarkdown, setCopiedMarkdown] = useState(false);

  // Real-time votes from all 3 committee members in Firestore
  const [allVotes, setAllVotes] = useState({});
  const [loadingVotes, setLoadingVotes] = useState(true);

  // Check URL ?code= or sessionStorage on mount
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search || "");
    const urlCode = (params.get("code") || "").trim().toUpperCase();
    if (urlCode && COMMITTEE_MEMBERS[urlCode]) {
      const member = COMMITTEE_MEMBERS[urlCode];
      setActiveMember(member);
      sessionStorage.setItem("we360_committee_code", urlCode);
      return;
    }
    const savedCode = sessionStorage.getItem("we360_committee_code");
    if (savedCode && COMMITTEE_MEMBERS[savedCode]) {
      setActiveMember(COMMITTEE_MEMBERS[savedCode]);
    }
  }, []);

  // Subscribe to committee_votes collection in Firestore
  useEffect(() => {
    const colRef = collection(db, "committee_votes");
    const unsub = onSnapshot(
      colRef,
      (snapshot) => {
        const map = {};
        snapshot.forEach((docSnap) => {
          map[docSnap.id] = docSnap.data();
        });
        setAllVotes(map);
        setLoadingVotes(false);
      },
      (err) => {
        console.error("Error leyendo votos del comité:", err);
        setLoadingVotes(false);
      }
    );
    return () => unsub();
  }, []);

  // Hydrate current member's saved vote when activeMember or allVotes loads
  useEffect(() => {
    if (!activeMember) return;
    const existing = allVotes[activeMember.id];
    if (existing) {
      setSelections(existing.selections || {});
      setComments(existing.comments || {});
      setGeneralNote(existing.generalNote || "");
    } else {
      // Pre-select recommended Option A by default so they can review or change
      const defaultSelections = {};
      QUESTIONS.forEach((q) => {
        defaultSelections[q.id] = "A";
      });
      setSelections((prev) =>
        Object.keys(prev).length > 0 ? prev : defaultSelections
      );
    }
  }, [activeMember, allVotes]);

  const handleUnlock = (e) => {
    e.preventDefault();
    const normalized = accessCodeInput.trim().toUpperCase();
    const member = COMMITTEE_MEMBERS[normalized];
    if (!member) {
      setCodeError(
        "Código de acceso no válido. Utiliza el código personal asignado para el Comité."
      );
      return;
    }
    setCodeError("");
    setActiveMember(member);
    if (typeof window !== "undefined") {
      sessionStorage.setItem("we360_committee_code", normalized);
      const url = new URL(window.location.href);
      url.searchParams.set("code", normalized);
      window.history.replaceState({}, "", url.toString());
    }
  };

  const handleLogoutCode = () => {
    if (typeof window !== "undefined") {
      sessionStorage.removeItem("we360_committee_code");
      const url = new URL(window.location.href);
      url.searchParams.delete("code");
      window.history.replaceState({}, "", url.toString());
    }
    setActiveMember(null);
    setAccessCodeInput("");
  };

  const handleSelectOption = (questionId, optionId) => {
    setSelections((prev) => ({
      ...prev,
      [questionId]: optionId,
    }));
    setSaveSuccess(false);
  };

  const handleCommentChange = (questionId, text) => {
    setComments((prev) => ({
      ...prev,
      [questionId]: text,
    }));
    setSaveSuccess(false);
  };

  const handleSaveVote = async () => {
    if (!activeMember) return;
    setSaving(true);
    setSaveSuccess(false);
    try {
      const payload = {
        memberId: activeMember.id,
        memberName: activeMember.name,
        memberRole: activeMember.role,
        selections,
        comments,
        generalNote,
        updatedAt: serverTimestamp(),
        updatedAtIso: new Date().toISOString(),
      };
      await setDoc(doc(db, "committee_votes", activeMember.id), payload, {
        merge: true,
      });
      setSaveSuccess(true);
    } catch (err) {
      console.error("Error guardando decisión del comité:", err);
      alert("No se pudo guardar el voto en Firestore: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const completedQuestionsCount = useMemo(() => {
    return QUESTIONS.filter((q) => Boolean(selections[q.id])).length;
  }, [selections]);

  const generateMarkdownSummary = () => {
    const lines = [
      "# Acta de Decisiones — Comité de Reglas de Negocio WE360",
      "**Tema:** Blindaje de Identidad, Correo Electrónico y Número de Teléfono (Web + Core Services + GCP FHIR)",
      `**Fecha de generación:** ${new Date().toLocaleString("es-PA")}`,
      "",
      "## Participantes del Comité",
    ];
    MEMBER_LIST.forEach((m) => {
      const v = allVotes[m.id];
      lines.push(
        `- **${m.name}** (${m.role}): ${
          v ? `Voto registrado (${v.updatedAtIso?.slice(0, 16).replace("T", " ") || "OK"})` : "Pendiente de votar"
        }`
      );
    });
    lines.push("", "---", "");

    QUESTIONS.forEach((q) => {
      lines.push(`## Decisión ${q.number}: ${q.title}`);
      lines.push(`**Pregunta:** ${q.question}`);
      lines.push("");
      MEMBER_LIST.forEach((m) => {
        const v = allVotes[m.id];
        const chosenId = v?.selections?.[q.id];
        const chosenOpt = q.options.find((o) => o.id === chosenId);
        const note = v?.comments?.[q.id];
        lines.push(
          `- **${m.name}:** ${
            chosenOpt ? chosenOpt.label : "Sin respuesta aún"
          }${note ? ` — *Comentario: "${note}"*` : ""}`
        );
      });
      lines.push("");
    });

    lines.push("## Observaciones Generales");
    MEMBER_LIST.forEach((m) => {
      const v = allVotes[m.id];
      if (v?.generalNote) {
        lines.push(`- **${m.name}:** ${v.generalNote}`);
      }
    });

    return lines.join("\n");
  };

  const handleCopyMarkdown = () => {
    const md = generateMarkdownSummary();
    navigator.clipboard.writeText(md);
    setCopiedMarkdown(true);
    setTimeout(() => setCopiedMarkdown(false), 3000);
  };

  // ============================================================================
  // GATE SCREEN: TEMPORARY ACCESS CODE
  // ============================================================================
  if (!activeMember) {
    return (
      <div className="min-h-screen bg-[#FCFAEE] text-slate-900 flex flex-col justify-center items-center p-4 sm:p-6">
        <div className="w-full max-w-lg bg-white/90 backdrop-blur-md border border-slate-200/80 rounded-3xl shadow-xl p-6 sm:p-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-2xl bg-[#00628F]/10 border border-[#00628F]/20 flex items-center justify-center text-[#00628F]">
              <KeyRound className="w-6 h-6" />
            </div>
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-[#2A9D8F] block">
                WE360 · Gobernanza de Arquitectura y Clínica
              </span>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
                Comité de Reglas de Negocio
              </h1>
            </div>
          </div>

          <p className="text-sm text-slate-600 leading-relaxed mb-6">
            Bienvenido a la sesión interactiva de toma de decisiones sobre{" "}
            <strong className="text-slate-800">
              Aislamiento de Identidad, Correo Electrónico y Número de Teléfono
              (Web + Core Services + GCP FHIR)
            </strong>
            . Ingresa tu código temporal de miembro del comité para revisar los
            diagramas, escenarios y registrar tu voto.
          </p>

          <form onSubmit={handleUnlock} className="space-y-4">
            <div>
              <label
                htmlFor="committee-code-input"
                className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2"
              >
                Código Temporal de Acceso
              </label>
              <input
                id="committee-code-input"
                data-testid="committee-code-input"
                type="text"
                value={accessCodeInput}
                onChange={(e) => setAccessCodeInput(e.target.value)}
                placeholder="Ej. JORGE-360"
                className="w-full px-4 py-3.5 rounded-2xl border border-slate-300 bg-white text-slate-900 font-mono text-base uppercase tracking-widest focus:outline-none focus:ring-2 focus:ring-[#00628F] focus:border-transparent"
                autoFocus
              />
              {codeError && (
                <p className="mt-2 text-xs font-medium text-rose-600 flex items-center gap-1.5">
                  <AlertTriangle className="w-4 h-4 shrink-0" />
                  {codeError}
                </p>
              )}
            </div>

            <button
              type="submit"
              data-testid="committee-unlock-btn"
              className="w-full py-3.5 px-6 rounded-2xl bg-[#00628F] hover:bg-[#004f73] text-white font-semibold text-sm shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <span>Ingresar al Panel del Comité</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-slate-200/80">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
              Miembros Convocados ({ Object.keys(allVotes).length } de 3 han votado)
            </p>
            <div className="space-y-2.5">
              {MEMBER_LIST.map((m) => {
                const hasVoted = Boolean(allVotes[m.id]);
                return (
                  <div
                    key={m.id}
                    className="flex items-center justify-between p-3 rounded-2xl bg-slate-50 border border-slate-200/70"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${m.badgeColor}`}
                      >
                        {m.avatar}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800">
                          {m.name}
                        </p>
                        <p className="text-xs text-slate-500">{m.role}</p>
                      </div>
                    </div>
                    <span
                      className={`text-xs font-semibold px-2.5 py-1 rounded-full ${
                        hasVoted
                          ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                          : "bg-amber-50 text-amber-700 border border-amber-200"
                      }`}
                    >
                      {hasVoted ? "Voto registrado" : "Pendiente"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // MAIN INTERACTIVE QUESTIONNAIRE & ARCHITECTURE BOARD
  // ============================================================================
  return (
    <div className="min-h-screen bg-[#FCFAEE] text-slate-900 pb-24">
      {/* Top Sticky Member Bar */}
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200/80 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-2xl flex items-center justify-center font-bold text-sm shadow-sm ${activeMember.badgeColor}`}
            >
              {activeMember.avatar}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-wider text-[#00628F]">
                  Sesión Activa del Comité
                </span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold">
                  {activeMember.code}
                </span>
              </div>
              <p className="text-sm sm:text-base font-bold text-slate-900">
                {activeMember.name}{" "}
                <span className="font-normal text-slate-500 text-xs sm:text-sm">
                  · {activeMember.role}
                </span>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <div className="hidden md:flex items-center gap-1.5 text-xs font-semibold text-slate-600 bg-slate-100 px-3 py-1.5 rounded-full">
              <CheckCircle2 className="w-4 h-4 text-[#2A9D8F]" />
              <span>
                {completedQuestionsCount} / {QUESTIONS.length} decisiones
                seleccionadas
              </span>
            </div>

            <button
              onClick={handleSaveVote}
              disabled={saving}
              data-testid="save-committee-vote-top"
              className="px-4 py-2 rounded-xl bg-[#00628F] hover:bg-[#004f73] text-white text-xs sm:text-sm font-semibold shadow-sm transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
              <span>{saving ? "Guardando..." : "Guardar mis decisiones"}</span>
            </button>

            <button
              onClick={handleLogoutCode}
              title="Cambiar código de participante"
              className="p-2 rounded-xl border border-slate-200 hover:bg-slate-100 text-slate-600 transition-colors cursor-pointer"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-8 space-y-8">
        {/* Hero / Executive Context Card */}
        <section className="bg-white/90 backdrop-blur-md border border-slate-200/80 rounded-3xl shadow-sm p-6 sm:p-8">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-rose-100 text-rose-800 border border-rose-200 flex items-center gap-1.5">
              <ShieldAlert className="w-3.5 h-3.5" />
              Caso de Estudio Real en DEV · 20 Oct 2026
            </span>
            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[#00628F]/10 text-[#00628F]">
              Arquitectura Web + Core Services + GCP FHIR R4
            </span>
          </div>

          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
            Blindaje de Identidad: Correo Electrónico vs. Número de Teléfono en
            WhatsApp
          </h1>
          <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-6">
            Durante las pruebas en el ambiente DEV encontramos un escenario
            crítico de cruce de información entre cuentas cuando dos usuarios
            distintos registran el mismo número de teléfono. A continuación se
            expone el hallazgo técnico, los diagramas de arquitectura y las{" "}
            <strong>5 decisiones clave de reglas de negocio</strong> que debemos
            acordar entre Producto, Dirección Médica y Core Services antes de
            implementar el candado definitivo.
          </p>

          {/* Incident Breakdown Box */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 bg-amber-50/70 border border-amber-200/90 rounded-2xl p-4 sm:p-5">
            <div className="bg-white rounded-xl p-4 border border-amber-200/60 shadow-xs">
              <span className="text-xs font-bold uppercase text-amber-800 block mb-1">
                1. Cuenta Antigua (14 Oct)
              </span>
              <p className="text-sm font-bold text-slate-900">
                test008@we360.net ("Jorge test")
              </p>
              <p className="text-xs text-slate-600 mt-1">
                Registró el teléfono{" "}
                <code className="font-mono font-bold text-slate-900 bg-slate-100 px-1.5 py-0.5 rounded">
                  50766778899
                </code>
                , completó F1–F5 (Fase 6) y Core Services le generó el empujón
                de las <strong>08:30 PM</strong>:{" "}
                <em>"CENA LIGERA: ENSALADA CON PROTEÍNA"</em>.
              </p>
            </div>

            <div className="bg-white rounded-xl p-4 border border-amber-200/60 shadow-xs">
              <span className="text-xs font-bold uppercase text-amber-800 block mb-1">
                2. Cuenta Nueva (20 Oct)
              </span>
              <p className="text-sm font-bold text-slate-900">
                test20261020727@we360.app ("Test Gallego")
              </p>
              <p className="text-xs text-slate-600 mt-1">
                Creó cuenta nueva, llenó{" "}
                <strong>únicamente F1 + Escaneo Facial</strong> e ingresó el
                mismo teléfono{" "}
                <code className="font-mono font-bold text-slate-900 bg-slate-100 px-1.5 py-0.5 rounded">
                  50766778899
                </code>{" "}
                (porque F1 no validaba teléfonos repetidos).
              </p>
            </div>

            <div className="bg-white rounded-xl p-4 border border-rose-200 shadow-xs">
              <span className="text-xs font-bold uppercase text-rose-700 block mb-1">
                3. Resultado en el Dashboard
              </span>
              <p className="text-sm font-bold text-rose-900">
                Fuga de Empujón Clínico por Teléfono
              </p>
              <p className="text-xs text-slate-600 mt-1">
                El Dashboard consultó{" "}
                <code className="font-mono text-[11px]">
                  ?phone=50766778899&email=test20261020727...
                </code>
                . Core Services priorizó la búsqueda por{" "}
                <code className="font-mono">phone</code> sin exigir que el{" "}
                <code className="font-mono">email</code> coincidiera y devolvió
                el <strong>Empujón Clínico Activo</strong> de{" "}
                <code className="font-mono">test008@we360.net</code>.
              </p>
            </div>
          </div>
        </section>

        {/* Interactive Visual Architecture Diagrams Section */}
        <section className="bg-white/90 backdrop-blur-md border border-slate-200/80 rounded-3xl shadow-sm p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-[#2A9D8F] block">
                Arquitectura E2E (Web + Core Services + GCP FHIR)
              </span>
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                Diagramas de Flujo del Incidente y Solución Propuesta
              </h2>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setActiveDiagramTab("incident")}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  activeDiagramTab === "incident"
                    ? "bg-rose-600 text-white shadow-xs"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                1. Flujo Actual (Vulnerabilidad)
              </button>
              <button
                type="button"
                onClick={() => setActiveDiagramTab("registration")}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  activeDiagramTab === "registration"
                    ? "bg-[#00628F] text-white shadow-xs"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                2. Registro Blindado + OTP
              </button>
              <button
                type="button"
                onClick={() => setActiveDiagramTab("dashboard")}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  activeDiagramTab === "dashboard"
                    ? "bg-[#2A9D8F] text-white shadow-xs"
                    : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
              >
                3. Lectura Blindada en Dashboard
              </button>
            </div>
          </div>

          {/* DIAGRAM 1: CURRENT VULNERABILITY */}
          {activeDiagramTab === "incident" && (
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 sm:p-6 space-y-6">
              <div className="flex items-center justify-between flex-wrap gap-2 border-b border-slate-200 pb-4">
                <div>
                  <h3 className="text-base font-bold text-rose-800 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-rose-600" />
                    Diagrama 1: Cómo ocurrió la filtración del Empujón Clínico
                    por número de teléfono
                  </h3>
                  <p className="text-xs text-slate-600 mt-0.5">
                    Interacción entre Plataforma Web (Firestore), GCP Healthcare
                    FHIR R4 y Core Services (WhatsApp Engine).
                  </p>
                </div>
                <span className="text-xs font-semibold px-3 py-1 rounded-full bg-rose-100 text-rose-800">
                  Estado Actual: Vulnerable a colisión de teléfono
                </span>
              </div>

              {/* 3 System Columns Header */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-3.5 rounded-xl bg-sky-50 border border-sky-200 flex items-center gap-3">
                  <Globe className="w-5 h-5 text-[#00628F] shrink-0" />
                  <div>
                    <p className="text-xs font-bold text-[#00628F] uppercase">
                      Capa 1 · Plataforma Web
                    </p>
                    <p className="text-xs text-slate-700">
                      React SPA + Firestore (we360-dev)
                    </p>
                  </div>
                </div>
                <div className="p-3.5 rounded-xl bg-teal-50 border border-teal-200 flex items-center gap-3">
                  <Database className="w-5 h-5 text-[#2A9D8F] shrink-0" />
                  <div>
                    <p className="text-xs font-bold text-[#2A9D8F] uppercase">
                      Capa 2 · GCP Healthcare API
                    </p>
                    <p className="text-xs text-slate-700">
                      FHIR R4 Store (System of Record)
                    </p>
                  </div>
                </div>
                <div className="p-3.5 rounded-xl bg-amber-50 border border-amber-200 flex items-center gap-3">
                  <Smartphone className="w-5 h-5 text-amber-700 shrink-0" />
                  <div>
                    <p className="text-xs font-bold text-amber-800 uppercase">
                      Capa 3 · Core Services
                    </p>
                    <p className="text-xs text-slate-700">
                      Motor Clínico & Bot WhatsApp
                    </p>
                  </div>
                </div>
              </div>

              {/* Sequence Steps */}
              <div className="space-y-3">
                <div className="p-4 rounded-xl bg-white border border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-slate-800 text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                      1
                    </span>
                    <div>
                      <p className="text-sm font-bold text-slate-900">
                        Estado Previo (14 Oct): Usuario A registra el teléfono{" "}
                        <code className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">
                          50766778899
                        </code>
                      </p>
                      <p className="text-xs text-slate-600 mt-0.5">
                        <code className="font-mono">test008@we360.net</code>{" "}
                        completa F1–F5. Core Services indexa{" "}
                        <code className="font-mono">
                          phone=50766778899 → email=test008@we360.net
                        </code>{" "}
                        (Fase 6) y genera el{" "}
                        <code className="font-mono">activeNudge</code> de las
                        08:30 PM (Cena Ligera).
                      </p>
                    </div>
                  </div>
                  <span className="text-xs font-mono px-2.5 py-1 rounded bg-slate-100 text-slate-700 shrink-0">
                    Core DB: 50766778899 = test008
                  </span>
                </div>

                <div className="p-4 rounded-xl bg-white border border-amber-300 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-amber-600 text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                      2
                    </span>
                    <div>
                      <p className="text-sm font-bold text-slate-900">
                        20 Oct: Usuario B (<code className="font-mono">test20261020727@we360.app</code>) llena F1 con el mismo teléfono
                      </p>
                      <p className="text-xs text-slate-600 mt-0.5">
                        En <code className="font-mono">/f1</code> escribe{" "}
                        <code className="font-mono">50766778899</code>.{" "}
                        <strong>Brecha #1:</strong> FormF1 guarda en Firestore y
                        crea un nuevo <code className="font-mono">FHIR Patient</code>{" "}
                        en GCP sin verificar que <code className="font-mono">50766778899</code>{" "}
                        ya pertenecía a otro usuario.
                      </p>
                    </div>
                  </div>
                  <span className="text-xs font-mono px-2.5 py-1 rounded bg-amber-100 text-amber-900 shrink-0">
                    Sin chequeo de unicidad en F1
                  </span>
                </div>

                <div className="p-4 rounded-xl bg-rose-50/80 border border-rose-300 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-rose-600 text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">
                      3
                    </span>
                    <div>
                      <p className="text-sm font-bold text-rose-950">
                        Usuario B abre su Dashboard → Cruce de datos desde Core
                        Services
                      </p>
                      <p className="text-xs text-rose-900 mt-0.5">
                        Web llama a{" "}
                        <code className="font-mono">
                          GET /api/user/daily-recommendations?phone=50766778899&email=test20261020727@we360.app
                        </code>
                        . <strong>Brecha #2:</strong> Core Services busca por{" "}
                        <code className="font-mono">phone=50766778899</code>, no
                        valida que el email de la petición coincida con{" "}
                        <code className="font-mono">test008@we360.net</code>, y
                        retorna el <code className="font-mono">activeNudge</code>{" "}
                        de la cuenta antigua. <strong>Brecha #3:</strong> El
                        widget de la Web pinta el{" "}
                        <code className="font-mono">activeNudge</code> arriba de
                        la tarjeta de bloqueo.
                      </p>
                    </div>
                  </div>
                  <span className="text-xs font-bold px-2.5 py-1 rounded bg-rose-200 text-rose-900 shrink-0">
                    Fuga de activeNudge
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* DIAGRAM 2: TARGET REGISTRATION & OTP */}
          {activeDiagramTab === "registration" && (
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 sm:p-6 space-y-5">
              <div className="flex items-center justify-between flex-wrap gap-2 border-b border-slate-200 pb-4">
                <div>
                  <h3 className="text-base font-bold text-[#00628F] flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-[#00628F]" />
                    Diagrama 2: Arquitectura Objetivo — Registro con Unicidad
                    Tripartita + Verificación OTP
                  </h3>
                  <p className="text-xs text-slate-600 mt-0.5">
                    Aplica por igual en Onboarding, Formulario F1, Mi Cuenta e
                    Importación B2B.
                  </p>
                </div>
                <span className="text-xs font-semibold px-3 py-1 rounded-full bg-sky-100 text-[#00628F]">
                  1 Teléfono = 1 Usuario Verificado
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
                  <span className="text-xs font-bold text-[#00628F] uppercase block mb-1">
                    Paso 1 · Normalización E.164
                  </span>
                  <p className="text-sm font-bold text-slate-900 mb-1">
                    Captura en Web (F1 / Onboarding / Mi Cuenta)
                  </p>
                  <p className="text-xs text-slate-600">
                    El teléfono se normaliza a formato canónico{" "}
                    <code className="font-mono">507XXXXXXXX</code> antes de
                    cualquier consulta.
                  </p>
                </div>

                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
                  <span className="text-xs font-bold text-[#00628F] uppercase block mb-1">
                    Paso 2 · Chequeo en 3 Capas
                  </span>
                  <p className="text-sm font-bold text-slate-900 mb-1">
                    Firestore + Core Services + GCP FHIR
                  </p>
                  <p className="text-xs text-slate-600">
                    Se verifica que el teléfono no pertenezca a otro{" "}
                    <code className="font-mono">uid/email</code> en Firestore,
                    Core DB ni en{" "}
                    <code className="font-mono">FHIR Patient.telecom</code>. Si
                    está ocupado → <strong>Rechazo 409</strong>.
                  </p>
                </div>

                <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
                  <span className="text-xs font-bold text-[#2A9D8F] uppercase block mb-1">
                    Paso 3 · Handshake WhatsApp
                  </span>
                  <p className="text-sm font-bold text-slate-900 mb-1">
                    Estado Inicial: phoneVerified = false
                  </p>
                  <p className="text-xs text-slate-600">
                    Core Services envía un PIN OTP de 6 dígitos por WhatsApp (o
                    solicita mensaje de activación desde ese número) para
                    comprobar propiedad real.
                  </p>
                </div>

                <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-200 shadow-xs">
                  <span className="text-xs font-bold text-emerald-800 uppercase block mb-1">
                    Paso 4 · Vinculación Sellada
                  </span>
                  <p className="text-sm font-bold text-emerald-950 mb-1">
                    phoneVerified = true en Todo el Ecosistema
                  </p>
                  <p className="text-xs text-emerald-900">
                    Se sella el vínculo{" "}
                    <code className="font-mono">(uid, email, phone)</code> en
                    Firestore, en el recurso <code className="font-mono">FHIR Patient</code>{" "}
                    y en Core Services.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* DIAGRAM 3: TARGET DASHBOARD SHIELD */}
          {activeDiagramTab === "dashboard" && (
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 sm:p-6 space-y-5">
              <div className="flex items-center justify-between flex-wrap gap-2 border-b border-slate-200 pb-4">
                <div>
                  <h3 className="text-base font-bold text-[#2A9D8F] flex items-center gap-2">
                    <Lock className="w-5 h-5 text-[#2A9D8F]" />
                    Diagrama 3: Arquitectura Objetivo — Lectura Blindada en
                    Dashboard (Identity Guard)
                  </h3>
                  <p className="text-xs text-slate-600 mt-0.5">
                    Doble candado en Backend (Core Services) y Frontend (Web
                    Dashboard) para impedir cualquier filtración.
                  </p>
                </div>
                <span className="text-xs font-semibold px-3 py-1 rounded-full bg-teal-100 text-teal-900">
                  Zero-Leak Guarantee
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-xl border border-slate-200">
                  <span className="text-xs font-bold text-[#00628F] uppercase block mb-1">
                    Candado 1 · Pre-Filtro en Web
                  </span>
                  <p className="text-sm font-bold text-slate-900 mb-1">
                    Verificación antes de consultar
                  </p>
                  <p className="text-xs text-slate-600">
                    Si el perfil del usuario en Firestore tiene{" "}
                    <code className="font-mono">phoneVerified !== true</code>,
                    el widget de WhatsApp ni siquiera consulta recomendaciones
                    clínicas: muestra directamente la tarjeta{" "}
                    <strong>"Verifica tu WhatsApp para activar tu asistente"</strong>.
                  </p>
                </div>

                <div className="bg-white p-4 rounded-xl border border-slate-200">
                  <span className="text-xs font-bold text-[#2A9D8F] uppercase block mb-1">
                    Candado 2 · Validación en Core Services
                  </span>
                  <p className="text-sm font-bold text-slate-900 mb-1">
                    Match Obligatorio: Email + Teléfono
                  </p>
                  <p className="text-xs text-slate-600">
                    Al recibir{" "}
                    <code className="font-mono">GET /daily-recommendations?phone=X&email=Y</code>,
                    Core Services verifica que el registro dueño de{" "}
                    <code className="font-mono">phone=X</code> tenga exactamente{" "}
                    <code className="font-mono">email === Y</code>. Si difiere,
                    devuelve{" "}
                    <code className="font-mono text-rose-700">
                      409 PHONE_OWNERSHIP_CONFLICT
                    </code>{" "}
                    con cero datos clínicos.
                  </p>
                </div>

                <div className="bg-white p-4 rounded-xl border border-slate-200">
                  <span className="text-xs font-bold text-emerald-700 uppercase block mb-1">
                    Candado 3 · Renderizado Coherente
                  </span>
                  <p className="text-sm font-bold text-slate-900 mb-1">
                    activeNudge atado a la Fase del Usuario
                  </p>
                  <p className="text-xs text-slate-600">
                    El <code className="font-mono">activeNudge</code> solo se
                    muestra si proviene del propio usuario autenticado y si la
                    cuenta cumple el umbral de perfilamiento clínico aprobado
                    por el Comité.
                  </p>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Live Committee Consensus Status Bar */}
        <section className="bg-white/90 backdrop-blur-md border border-slate-200/80 rounded-3xl shadow-sm p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-[#00628F] flex items-center gap-1.5">
                <Users className="w-4 h-4" />
                Tablero en Tiempo Real (Firestore we360-dev)
              </span>
              <h2 className="text-xl font-bold text-slate-900 mt-0.5">
                Estado de Votación y Consenso del Comité
              </h2>
            </div>

            <button
              type="button"
              onClick={handleCopyMarkdown}
              className="px-4 py-2.5 rounded-xl border border-slate-300 bg-white hover:bg-slate-50 text-slate-800 text-xs font-bold flex items-center gap-2 self-start sm:self-auto cursor-pointer shadow-2xs"
            >
              {copiedMarkdown ? (
                <>
                  <Check className="w-4 h-4 text-emerald-600" />
                  <span className="text-emerald-700">
                    ¡Acta Markdown copiada al portapapeles!
                  </span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 text-[#00628F]" />
                  <span>Copiar Acta Consolidada (Markdown)</span>
                </>
              )}
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {MEMBER_LIST.map((m) => {
              const voteDoc = allVotes[m.id];
              const isMe = activeMember.id === m.id;
              return (
                <div
                  key={m.id}
                  className={`p-4 rounded-2xl border transition-all ${
                    isMe
                      ? "bg-[#00628F]/5 border-[#00628F]"
                      : "bg-slate-50/80 border-slate-200"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2.5">
                      <div
                        className={`w-8 h-8 rounded-xl flex items-center justify-center text-xs font-bold ${m.badgeColor}`}
                      >
                        {m.avatar}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-slate-900">
                          {m.name} {isMe && "(Tú)"}
                        </p>
                        <p className="text-[11px] text-slate-500">{m.role}</p>
                      </div>
                    </div>
                  </div>

                  {voteDoc ? (
                    <div className="mt-3 pt-3 border-t border-slate-200/80 space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-semibold text-emerald-700 flex items-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          Voto guardado
                        </span>
                        <span className="text-slate-400 font-mono text-[11px]">
                          {voteDoc.updatedAtIso
                            ? new Date(voteDoc.updatedAtIso).toLocaleTimeString(
                                "es-PA",
                                { hour: "2-digit", minute: "2-digit" }
                              )
                            : "Reciente"}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {QUESTIONS.map((q) => {
                          const pick = voteDoc.selections?.[q.id] || "-";
                          return (
                            <span
                              key={q.id}
                              className={`text-xs font-bold px-2 py-0.5 rounded-lg border ${
                                pick === "A"
                                  ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                                  : pick === "B"
                                  ? "bg-amber-50 text-amber-800 border-amber-200"
                                  : "bg-sky-50 text-sky-800 border-sky-200"
                              }`}
                            >
                              P{q.number}: Opción {pick}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3 pt-3 border-t border-slate-200/80 flex items-center justify-between text-xs text-slate-500">
                      <span>Aún no ha enviado su votación</span>
                      <span className="px-2 py-0.5 rounded bg-slate-200/70 text-slate-600 font-medium">
                        Pendiente
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* The 5 Interactive Questions */}
        <section className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-[#2A9D8F] block">
                Cuestionario de Reglas de Negocio
              </span>
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900">
                5 Escenarios a Definir por el Comité
              </h2>
            </div>
          </div>

          {QUESTIONS.map((q) => {
            const currentPick = selections[q.id] || "";
            const currentComment = comments[q.id] || "";

            return (
              <div
                key={q.id}
                data-testid={`question-card-${q.number}`}
                className="bg-white/95 backdrop-blur-md border border-slate-200/90 rounded-3xl shadow-sm overflow-hidden"
              >
                {/* Question Header */}
                <div className="bg-slate-50/90 border-b border-slate-200/80 px-6 py-4 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-xl bg-[#00628F] text-white font-bold text-sm flex items-center justify-center">
                      {q.number}
                    </span>
                    <div>
                      <span className="text-[11px] font-bold uppercase tracking-wider text-[#2A9D8F] block">
                        {q.category}
                      </span>
                      <h3 className="text-base sm:text-lg font-bold text-slate-900">
                        {q.title}
                      </h3>
                    </div>
                  </div>

                  {/* Mini badges showing what each member voted on this question */}
                  <div className="flex items-center gap-1.5">
                    {MEMBER_LIST.map((m) => {
                      const mPick = allVotes[m.id]?.selections?.[q.id];
                      if (!mPick) return null;
                      return (
                        <span
                          key={m.id}
                          title={`${m.name} votó Opción ${mPick}`}
                          className="text-xs font-bold px-2.5 py-1 rounded-full bg-white border border-slate-200 text-slate-700 shadow-2xs"
                        >
                          {m.avatar}:{" "}
                          <strong className="text-[#00628F]">{mPick}</strong>
                        </span>
                      );
                    })}
                  </div>
                </div>

                <div className="p-6 space-y-5">
                  {/* Main Question Prompt */}
                  <p className="text-base font-semibold text-slate-900 leading-snug">
                    {q.question}
                  </p>

                  {/* Illustrative Scenario Box */}
                  <div className="bg-[#FCFAEE] border border-amber-200/90 rounded-2xl p-4 sm:p-5 space-y-2">
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-amber-900">
                      <Sparkles className="w-4 h-4 text-amber-600 shrink-0" />
                      <span>{q.scenario.title}</span>
                    </div>
                    <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
                      {q.scenario.description}
                    </p>
                    <p className="text-xs font-medium text-amber-900 bg-amber-100/70 px-3 py-2 rounded-xl border border-amber-200/60">
                      <strong>Evidencia en DEV:</strong>{" "}
                      {q.scenario.incidentRef}
                    </p>
                  </div>

                  {/* Technical Impact Pills */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                    <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/70">
                      <strong className="text-[#00628F] block mb-0.5">
                        Impacto en Plataforma Web:
                      </strong>
                      <span className="text-slate-600">{q.impact.web}</span>
                    </div>
                    <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/70">
                      <strong className="text-amber-800 block mb-0.5">
                        Impacto en Core Services:
                      </strong>
                      <span className="text-slate-600">{q.impact.core}</span>
                    </div>
                    <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/70">
                      <strong className="text-[#2A9D8F] block mb-0.5">
                        Impacto en GCP FHIR R4:
                      </strong>
                      <span className="text-slate-600">{q.impact.fhir}</span>
                    </div>
                  </div>

                  {/* Selectable Options */}
                  <div className="space-y-3 pt-1">
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                      Selecciona tu postura ({activeMember.name}):
                    </p>
                    {q.options.map((opt) => {
                      const isSelected = currentPick === opt.id;
                      return (
                        <label
                          key={opt.id}
                          onClick={() => handleSelectOption(q.id, opt.id)}
                          className={`block p-4 rounded-2xl border-2 transition-all cursor-pointer ${
                            isSelected
                              ? "border-[#00628F] bg-[#00628F]/5 shadow-xs"
                              : "border-slate-200 hover:border-slate-300 bg-white"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <input
                              type="radio"
                              name={q.id}
                              value={opt.id}
                              checked={isSelected}
                              onChange={() => handleSelectOption(q.id, opt.id)}
                              className="mt-1 h-4 w-4 text-[#00628F] focus:ring-[#00628F]"
                            />
                            <div className="flex-1">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="text-sm font-bold text-slate-900">
                                  {opt.label}
                                </span>
                                {opt.recommended && (
                                  <span className="text-[11px] font-bold px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">
                                    Sugerida
                                  </span>
                                )}
                              </div>
                              <p className="text-xs sm:text-sm text-slate-600 mt-1 leading-relaxed">
                                {opt.detail}
                              </p>
                            </div>
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  {/* Comment / Note per Question */}
                  <div className="pt-2">
                    <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center gap-1.5">
                      <MessageSquare className="w-3.5 h-3.5 text-[#00628F]" />
                      <span>
                        Observaciones o matices de {activeMember.name} sobre la
                        Decisión #{q.number} (Opcional):
                      </span>
                    </label>
                    <textarea
                      rows={2}
                      value={currentComment}
                      onChange={(e) =>
                        handleCommentChange(q.id, e.target.value)
                      }
                      placeholder="Escribe aquí cualquier condición técnica, clínica o de experiencia de usuario..."
                      className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 bg-white text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#00628F]"
                    />
                  </div>

                  {/* Other committee members' comments on this question if any */}
                  {MEMBER_LIST.some(
                    (m) => m.id !== activeMember.id && allVotes[m.id]?.comments?.[q.id]
                  ) && (
                    <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-200 space-y-2">
                      <p className="text-xs font-bold text-slate-600 uppercase tracking-wider">
                        Comentarios de otros miembros en esta pregunta:
                      </p>
                      {MEMBER_LIST.map((m) => {
                        if (m.id === activeMember.id) return null;
                        const otherComment = allVotes[m.id]?.comments?.[q.id];
                        if (!otherComment) return null;
                        return (
                          <div
                            key={m.id}
                            className="text-xs text-slate-700 bg-white p-2.5 rounded-lg border border-slate-200/80"
                          >
                            <strong className="text-slate-900">
                              {m.name}:
                            </strong>{" "}
                            {otherComment}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </section>

        {/* Final General Notes & Submit Box */}
        <section className="bg-white/95 backdrop-blur-md border border-slate-200/90 rounded-3xl shadow-md p-6 sm:p-8 space-y-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[#2A9D8F]/15 text-[#2A9D8F] flex items-center justify-center">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900">
                Comentario General y Registro Oficial de Voto
              </h3>
              <p className="text-xs text-slate-600">
                Tus respuestas quedan guardadas en tiempo real en Firestore (
                <code className="font-mono">committee_votes/{activeMember.id}</code>
                ) y son visibles para los demás miembros del comité.
              </p>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-700 mb-2">
              Conclusión general o prioridad de implementación ({activeMember.name})
            </label>
            <textarea
              rows={3}
              value={generalNote}
              onChange={(e) => {
                setGeneralNote(e.target.value);
                setSaveSuccess(false);
              }}
              placeholder="Ej. Estoy de acuerdo con implementar el bloqueo 409 de inmediato en Core Services y dejar el OTP por WhatsApp para el siguiente sprint..."
              className="w-full px-4 py-3 rounded-2xl border border-slate-300 bg-white text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#00628F]"
            />
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
            <div className="text-xs text-slate-600">
              {saveSuccess ? (
                <span className="inline-flex items-center gap-1.5 font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-xl">
                  <CheckCircle2 className="w-4 h-4" />
                  ¡Tus decisiones han sido registradas exitosamente en el
                  tablero del Comité!
                </span>
              ) : (
                <span>
                  Has respondido{" "}
                  <strong>
                    {completedQuestionsCount} de {QUESTIONS.length}
                  </strong>{" "}
                  preguntas.
                </span>
              )}
            </div>

            <button
              type="button"
              onClick={handleSaveVote}
              disabled={saving}
              data-testid="save-committee-vote-bottom"
              className="px-6 py-3.5 rounded-2xl bg-[#00628F] hover:bg-[#004f73] text-white font-bold text-sm shadow-md transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4" />
              )}
              <span>
                {saving
                  ? "Guardando en Firestore..."
                  : `Guardar Voto Oficial de ${activeMember.name}`}
              </span>
            </button>
          </div>
        </section>
      </div>
    </div>
  );
}
